import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider,
  FormControlLabel, Stack, Switch, Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

export default function IntegrationsSettings({ profileApi, profile, onProfileChange }) {
  const [emailEnabled, setEmailEnabled] = useState(Boolean(profile?.email_integration_enabled));
  const [whatsappEnabled, setWhatsappEnabled] = useState(Boolean(profile?.whatsapp_integration_enabled));
  const [saving, setSaving] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  async function toggleEmail(enabled) {
    setEmailEnabled(enabled);
    setSaving('email');
    try {
      const res = await profileApi.updateIntegrations({ email_integration_enabled: enabled });
      onProfileChange?.({ email_integration_enabled: res.email_integration_enabled ?? enabled });
      showSnack(enabled ? 'Email integration enabled' : 'Email integration disabled');
    } catch (err) {
      setEmailEnabled(!enabled);
      showSnack(err.message || 'Failed to update setting', 'error');
    } finally {
      setSaving('');
    }
  }

  return (
    <Box>
      {snack.open && (
        <Alert
          severity={snack.severity}
          sx={{ mb: 2 }}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      )}

      <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Sharing Integrations</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Enable integrations to send photos directly to recipients from the event page.
        When enabled, a Send button appears on each request in your event.
      </Typography>

      {/* Email integration */}
      <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{
            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
            bgcolor: emailEnabled ? '#e3f2fd' : 'grey.100',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}>
            <EmailIcon sx={{ color: emailEnabled ? '#1976d2' : 'text.disabled' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Email</Typography>
                <Typography variant="body2" color="text.secondary">
                  Send a personalised email with a secure share link to each recipient.
                  Uses your event's Resend email delivery.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailEnabled}
                    onChange={e => toggleEmail(e.target.checked)}
                    disabled={saving === 'email'}
                  />
                }
                label={saving === 'email' ? <CircularProgress size={14} /> : emailEnabled ? 'On' : 'Off'}
                labelPlacement="start"
                sx={{ m: 0, gap: 1 }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* WhatsApp integration */}
      <Box sx={{ p: 2.5, border: '1px dashed', borderColor: 'divider', borderRadius: 2, mb: 2, opacity: 0.7 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{
            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
            bgcolor: 'grey.100',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WhatsAppIcon sx={{ color: 'text.disabled' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" fontWeight={700}>WhatsApp</Typography>
                  <Box
                    sx={{
                      px: 1, py: 0.25, borderRadius: 1,
                      bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200',
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="warning.700">Coming soon</Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Send photos directly via WhatsApp to recipients who provided their WhatsApp number.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                disabled
                sx={{ textTransform: 'none', flexShrink: 0, ml: 2 }}
              >
                Coming soon
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Integration settings apply to all your events. Recipients receive a secure, personalised link — no account required to view their photos.
      </Typography>
    </Box>
  );
}
